import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, MenuItem, Slider } from '@material-ui/core';
import { VolumeUp } from '@material-ui/icons';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import messagesToast from '~/validations/messagesNotifications';
import * as lib from '~/dependencies';
import { log, getMidiaDevicesByGroupId } from '~/helpers';

import { BoxDevices, ListDevices, BoxSelect, VolumeComponent } from './styles';

export default function DeviceManager({
  disableRingDevice,
  disableAudioTest,
  showSpeakerVolume,
}) {
  // SELECTORS
  const phoneDevice = useSelector(state => state?.system?.deviceManager);
  const ringDevice = useSelector(state => state?.system?.ringDevice);
  const volume = useSelector(state => state.system?.volume);

  // STATES
  const [microphones, setMicrophones] = useState([]);
  const [audio, setAudio] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [volumeValue, setVolumeValue] = useState(volume || 0.5);

  // MEMOs
  const microphoneId = useMemo(() => phoneDevice?.audio?.microphone?.deviceId, [
    phoneDevice,
  ]);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  async function getAvailableDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (devices) {
        setMicrophones(getMidiaDevicesByGroupId(devices, 'audioinput'));
        setSpeakers(getMidiaDevicesByGroupId(devices, 'audiooutput'));
      }
    } catch (err) {
      log.error('Error to access available media devices:', err);
    }
  }

  const getMicrophone = useCallback(async mic => {
    try {
      const audioValue = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: mic } },
        video: false,
      });
      setAudio(audioValue);
    } catch (err) {
      log.error('Error to access microphone devices: ', err);
    }
  }, []);

  function stopMicrophone() {
    audio.getTracks().forEach(track => track.stop());
    setAudio(null);
  }

  useEffect(() => {
    getAvailableDevices();
    getMicrophone(microphoneId);
  }, [getMicrophone, microphoneId, phoneDevice]);

  function changeDevice(device, type) {
    if (phoneDevice?.audio?.speaker?.deviceId) {
      let microphone;
      let speaker;

      const mediaDeviceInfoToObject = d => ({
        deviceId: d.deviceId,
        groupId: d.groupId,
        label: d.label,
      });

      switch (type) {
        case 'microphone':
          // Change the microfone and keep the selected speakers
          microphone = mediaDeviceInfoToObject(device);
          speaker = mediaDeviceInfoToObject(phoneDevice.audio.speaker);
          lib.ToastMessage(
            'success',
            messagesToast().SYSTEM.microphone.change.success
          );
          break;
        case 'ringDevice':
          microphone = mediaDeviceInfoToObject(phoneDevice.audio.microphone);
          speaker = mediaDeviceInfoToObject(phoneDevice.audio.speaker);
          dispatch(actions.ringDeviceSelect(device));
          lib.ToastMessage(
            'success',
            messagesToast().SYSTEM.ringDevice.change.success
          );
          break;
        default:
          microphone = mediaDeviceInfoToObject(phoneDevice.audio.microphone);
          speaker = mediaDeviceInfoToObject(device);
          lib.ToastMessage(
            'success',
            messagesToast().SYSTEM.speaker.change.success
          );
          break;
      }

      dispatch(
        actions.phoneDeviceChange({
          audio: { microphone, speaker },
        })
      );
    } else {
      getAvailableDevices();
    }
  }

  function doesNotShowDefault(label) {
    // Not using Default - because of multi language systems
    if (label?.includes(' - ')) {
      return label.split(' - ')[1];
    }
    return label;
  }

  function volumeHandler(event, value) {
    if (value >= 0.2) {
      setVolumeValue(value);
      dispatch(actions.volumeControl(value));
    }
  }

  return (
    <BoxDevices>
      <ListDevices>
        <div className="title">
          <div />
          <span>
            {t('screens.configuration.components.deviceManager.microphone')}
          </span>
          <div />
        </div>
        <BoxSelect>
          <Select
            className="inputSelect"
            value={
              phoneDevice?.audio?.microphone?.deviceId &&
              microphones?.some(
                i => i.deviceId === phoneDevice?.audio.microphone.deviceId
              )
                ? phoneDevice.audio.microphone.deviceId
                : 'default'
            }
            onChange={value =>
              changeDevice(
                microphones.filter(e => e.deviceId === value.target.value)[0],
                'microphone'
              )
            }>
            {microphones?.map(e => (
              <MenuItem key={e.deviceId} value={e.deviceId}>
                {doesNotShowDefault(e.label)}
              </MenuItem>
            ))}
          </Select>

          {audio && (
            <NuacomComponent.MicrophoneAudioAnalyzer
              dashes
              stopMicrophone={stopMicrophone}
              audio={audio}
            />
          )}
        </BoxSelect>
      </ListDevices>

      <ListDevices>
        <div className="title">
          <div />
          <span>
            {t('screens.configuration.components.deviceManager.speaker')}
          </span>
          <div />
        </div>
        <BoxSelect>
          <Select
            className="inputSelect"
            value={
              phoneDevice?.audio?.speaker?.deviceId &&
              speakers?.some(
                i => i.deviceId === phoneDevice?.audio?.speaker?.deviceId
              )
                ? phoneDevice?.audio?.speaker?.deviceId
                : 'default'
            }
            onChange={value =>
              changeDevice(
                speakers.filter(e => e.deviceId === value.target.value)[0],
                'speaker'
              )
            }>
            {speakers?.map(e => (
              <MenuItem key={e.deviceId} value={e.deviceId}>
                {doesNotShowDefault(e.label)}
              </MenuItem>
            ))}
          </Select>
          {showSpeakerVolume && (
            <VolumeComponent>
              <VolumeUp style={{ color: '#6c6c6c', marginRight: 10 }} />
              <Slider
                style={{ marginRight: 5 }}
                defaultValue={0.5}
                min={0.1}
                max={1}
                step={0.1}
                value={volumeValue}
                onChange={volumeHandler}
              />
            </VolumeComponent>
          )}
        </BoxSelect>
        {!disableAudioTest && (
          <NuacomComponent.PlayTest
            speaker
            deviceId={phoneDevice?.audio?.speaker?.deviceId}
          />
        )}
      </ListDevices>

      {!disableRingDevice && (
        <ListDevices style={{ marginTop: 0 }}>
          <div className="title">
            <div />
            <span>
              {t('screens.configuration.components.deviceManager.ringDevice')}
            </span>
            <div />
          </div>
          <BoxSelect>
            <Select
              className="inputSelect"
              value={
                (ringDevice?.deviceId &&
                  speakers?.some(i => i.deviceId === ringDevice.deviceId) &&
                  ringDevice.deviceId) ||
                phoneDevice?.audio?.speaker?.deviceId ||
                'default'
              }
              onChange={value =>
                changeDevice(
                  speakers.filter(e => e.deviceId === value.target.value)[0],
                  'ringDevice'
                )
              }>
              {speakers?.map(e => (
                <MenuItem key={e.deviceId} value={e.deviceId}>
                  {doesNotShowDefault(e.label)}
                </MenuItem>
              ))}
            </Select>
          </BoxSelect>

          <NuacomComponent.PlayTest
            deviceId={
              (ringDevice?.deviceId &&
                speakers?.some(i => i.deviceId === ringDevice.deviceId) &&
                ringDevice.deviceId) ||
              phoneDevice?.audio?.speaker?.deviceId
            }
          />
        </ListDevices>
      )}
    </BoxDevices>
  );
}

DeviceManager.defaultProps = {
  disableRingDevice: false,
  disableAudioTest: false,
  showSpeakerVolume: false,
};

DeviceManager.propTypes = {
  disableRingDevice: lib.PropTypes.bool,
  disableAudioTest: lib.PropTypes.bool,
  showSpeakerVolume: lib.PropTypes.bool,
};
